import { ComponentConfig } from '@measured/puck';
import React from 'react';
import {
  MarginProps,
  marginFields,
} from '../../../page-builder/puck/reusable-props/margin';
import CustomTooltip from '../../../components/common/tooltip';
import './../text-editor/style.scss';
import { paddingFields, PaddingProps } from '../../puck/reusable-props/padding';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getQuillEditorText,
} from '../../utils/helper';
import { ReduxSelectorProps } from '../../services/constants';
import PopUpFooter from '../../../components/common/PopUpFooter';
import { useState } from 'react';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import FooterFields, { FooterFieldsProps } from './fields/footerFields';
import showComponent, {
  showComponentProps,
} from '../../puck/reusable-props/showComponent';
import FooterArrayFields, {
  FooterArrayFieldsProps,
} from './fields/FooterArrayFields';

export interface customizationProps {
  gap: string;
  justifyContent: string;
}

export type FooterProps = MarginProps &
  showComponentProps &
  PaddingProps & {
    border: IBorderValueProps;
    customization: FooterFieldsProps;
  } & {
    footer: FooterArrayFieldsProps[];
  };

export const Footer = ({
  useAppSelector,
}: ReduxSelectorProps): ComponentConfig<FooterProps> => {
  return {
    label: (
      <CustomTooltip title="footer">
        <span>Footer</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...showComponent,
      footer: {
        type: 'custom',
        label: 'Footer',
        render: ({ value, onChange }) => {
          return <FooterArrayFields value={value} onChange={onChange} />;
        },
      },
      ...paddingFields,
      ...marginFields,
      border: {
        type: 'custom',
        label: 'Border',
        render: ({ value, onChange }) => (
          <Border
            value={value}
            onChange={value => onChange({ ...value })}
            title="Border"
          />
        ),
      },

      customization: {
        type: 'custom',
        label: 'Customization',
        render: ({ value, onChange }) => {
          return <FooterFields value={value} onChange={onChange} />;
        },
      },
    },
    defaultProps: {
      padding: {
        deviceType: 'desktop',
        paddingBottom: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingLeft: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingRight: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
        paddingTop: {
          desktop: '0',
          mobile: '0',
          tablet: '0',
        },
      },
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      footer: [
        {
          content: {
            viewMoreTerms: false,
            contentValue:
              '<h3 style="line-height:1.5;"><a href="#" rel="noopener noreferrer" target="_blank" style="font-size: 18px;">Privacy Policy</a></h3>',
            popUpContent:
              '<p style="line-height:1.5;"><span style="font-size: 16px;">We value your privacy and are committed to protecting your personal information. Any data we collect is used to improve your experience and provide our services. Your information is stored securely and shared only with trusted partners when necessary.</span></p>',
          },
        },
        {
          content: {
            viewMoreTerms: false,
            contentValue:
              '<h3 style="line-height:1.5;"><a href="#" rel="noopener noreferrer" target="_blank">Refund Policy</a></h3>',
            popUpContent:
              '<p style="line-height:1.5;"><span style="font-size: 16px;">We offer refunds for eligible products or services within a specified timeframe. Refund requests must include proof of purchase and meet our conditions. Please contact customer support for assistance with your refund.</span></p>',
          },
        },
      ],
      ...borderProps,
      showComponent: ['desktop', 'tablet', 'mobile'],
      customization: {
        currentDevice: 'desktop',
        mobile: {
          gap: 'gap-4',
          justifyContent: 'justify-center',
          divider: 'yes',
          dividerColor: '#000000',
        },
        tablet: {
          gap: 'gap-4',
          justifyContent: 'justify-center',
          divider: 'yes',
          dividerColor: '#000000',
        },
        desktop: {
          gap: 'gap-4',
          justifyContent: 'justify-center',
          divider: 'yes',
          dividerColor: '#000000',
        },
      },
    },
    render: ({
      margin,
      padding,
      editMode,
      border,
      customization,
      footer,
      showComponent,
    }) => {
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const [popUp, setUpPopUp] = useState<{
        open: boolean;
        popUpContent: string;
        headingContent: string;
      }>({
        open: false,
        popUpContent: '',
        headingContent: '',
      });
      const state = useAppSelector(state => state);

      return showComponent?.includes(currentDeviceType) ? (
        <>
          <section
            style={{
              ...marginStyles,
              ...getPaddingStyles(padding, currentDeviceType),
              ...getBorderProps(border, currentDeviceType),
            }}
            className={`flex items-center text-editor-content ${
              customization?.[currentDeviceType]?.justifyContent
            } ${customization?.[currentDeviceType]?.gap}
            `}>
            {footer?.map((item, index) => (
              <React.Fragment key={index}>
                <div
                  style={{
                    cursor: 'pointer',
                    lineHeight: 1.5,
                  }}
                  onClick={event => {
                    if (item?.content?.viewMoreTerms) {
                      event.preventDefault();
                      setUpPopUp({
                        open: true,
                        popUpContent: getQuillEditorText(
                          item?.content?.popUpContent || '',
                          state,
                        ),
                        headingContent: getQuillEditorText(
                          item?.content?.contentValue?.replace(
                            /<\/?[^>]+(>|$)/g,
                            '',
                          ) || '',
                          state,
                        ),
                      });
                    }
                  }}
                  dangerouslySetInnerHTML={{
                    __html: getQuillEditorText(
                      item?.content?.contentValue,
                      state,
                    ),
                  }}
                />
                {customization?.[currentDeviceType]?.divider === 'yes' &&
                  footer?.length !== index + 1 && (
                    <div
                      className={`border-r-[3px] h-5`}
                      style={{
                        borderColor:
                          customization?.[currentDeviceType]?.dividerColor,
                      }}
                    />
                  )}
              </React.Fragment>
            ))}
          </section>
          <PopUpFooter
            heading={popUp?.headingContent}
            popupContent={popUp?.popUpContent}
            handleClose={() => {
              setUpPopUp({
                open: false,
                popUpContent: '',
                headingContent: '',
              });
            }}
            open={popUp.open}
          />
        </>
      ) : (
        <></>
      );
    },
  };
};
